import type { Option } from "@tento-lona/sheets";
import { OrderedTree } from "@tento-core/ordered-tree";
import { Rational } from "@tento-core/rational";
import { RowId, UserId } from "@tento-lona/sheets";
import { RowKey, RowNetworkKey } from "@tento-lona/sheets";
import { RowLocalId } from "@tento-lona/sheets";
import { LookupKeyOwner } from "../client/lookup-key-owner.ts";
import { SheetRow } from "./sheet-row.ts";
import { type ChildSpec, Json } from "@tento-lona/sheets";
import { Network } from "../network.ts";
import { RowTypeSpecOps } from "@tento-lona/sheets/platform";

// ── Type-state markers ───────────────────────────────────────────

export interface Resolvable {
  readonly resolved: boolean;
}

export const Unresolved: Resolvable = { resolved: false };
export const Resolved: Resolvable = { resolved: true };

// ── Tree ─────────────────────────────────────────────────────────

type Node = OrderedTree.Node<RowLocalId, Rational>;

export namespace RowNode {
  /**
   * Row input for `RowNode.decompose` — structurally compatible with both
   * SDK and lona-js Network.SheetRow (they differ only in their RowId types).
   */
  export type DecomposableRow = SheetRow.Input & {
    id: { toString(): string };
    position: Rational | { n: number; d: number };
    children?: Option<Array<{ toString(): string }>>;
  };

  export function decompose(
    rows: DecomposableRow[],
    idRegistry: RowLocalId.Registry,
    metadata: SheetRow.Registry,
  ): OrderedTree.Flattened<RowLocalId, Rational> {
    const children = new Map<RowLocalId, RowLocalId[]>();
    const parent = new Map<RowLocalId, RowLocalId>();
    const positions = new Map<RowLocalId, Rational>();

    for (const row of rows) {
      const key = RowKey.of(row.id.toString());
      const localId = idRegistry.resolve(key);

      if (row.lookupKey) {
        // One lookup key, one owner: a persisted alias whose data key is this
        // lookupKey joins the reader that owns it rather than re-binding the
        // key on every hydrate (see `LookupKeyOwner`).
        LookupKeyOwner.claim(
          idRegistry,
          metadata,
          row.lookupKey,
          localId,
          row.type,
          "hydrate",
        );
      }

      metadata.set(localId, row, null);

      const position =
        row.position instanceof Rational
          ? row.position
          : Rational.make(row.position);
      positions.set(localId, position);

      if (row.children) {
        const childIds: RowLocalId[] = [];
        for (const childRaw of row.children) {
          const childKey = RowKey.of(childRaw.toString());
          const childId = idRegistry.resolve(childKey);
          childIds.push(childId);
          parent.set(childId, localId);
        }
        children.set(localId, childIds);
      }
    }

    return { children, parent, positions };
  }

  export class Tree<_S extends Resolvable> {
    readonly inner: OrderedTree.Root<RowLocalId, Rational>;
    readonly metadata: SheetRow.Registry;

    constructor(
      inner: OrderedTree.Root<RowLocalId, Rational>,
      metadata: SheetRow.Registry,
    ) {
      this.inner = inner;
      this.metadata = metadata;
    }

    get nodes(): Node[] {
      return this.inner.nodes;
    }

    get(id: RowLocalId): Option<Node> {
      return this.inner.get(id) ?? null;
    }

    parent(id: RowLocalId): Option<RowLocalId> {
      return this.inner.parent(id) ?? null;
    }

    children(id: RowLocalId): Node[] {
      return this.inner.children(id);
    }

    print(label: Option<(node: Node) => string>): string {
      return this.inner.print(label ?? undefined);
    }
  }

  export function resolveTree(
    tree: Tree<typeof Unresolved>,
    idRegistry: RowLocalId.Registry,
    metadata: SheetRow.Registry,
  ): Tree<typeof Resolved> {
    const byId = new Map<RowLocalId, Node>();
    const parentMap = new Map<RowLocalId, Option<RowLocalId>>();

    const cloneNode = (node: Node): Node => {
      const cloned: Node = {
        id: node.id,
        position: node.position,
        children: node.children.map(cloneNode),
      };
      byId.set(cloned.id, cloned);
      return cloned;
    };

    const roots = tree.nodes.map(cloneNode);

    const walk = (node: Node, parentId: Option<RowLocalId>) => {
      if (parentId) parentMap.set(node.id, parentId);
      for (const child of node.children) {
        walk(child, node.id);
      }
    };
    for (const root of roots) walk(root, null);

    const expandLeaves = (node: Node) => {
      if (node.children.length > 0) {
        for (const child of node.children) expandLeaves(child);
        return;
      }

      const sheetRow = metadata.get(node.id);
      if (!sheetRow) return;

      const hex = node.id.key.toHex();
      if (!hex) return;

      const specs = sheetRow.childSpecs;
      if (specs.length === 0) return;

      const visible = RowTypeSpecOps.resolveChildren(
        specs,
        sheetRow.reconciledAttributes,
      );
      // Only graft child-placement specs — root-placement specs are system rows
      const childPlacement = visible.filter((s) => s.placement === "child");
      if (childPlacement.length === 0) return;

      graftChildSpecs(
        childPlacement,
        node,
        sheetRow,
        hex,
        idRegistry,
        byId,
        parentMap,
        metadata,
      );
    };

    for (const root of roots) expandLeaves(root);

    const inner = new OrderedTree.Root(roots, byId, parentMap);
    return new Tree(inner, metadata);
  }
}

const TIME_SCALES: readonly Network.TimeScaleCustom[] = [
  "year",
  "month",
  "week",
  "day",
  "custom",
];

function graftChildSpecs(
  specs: readonly ChildSpec[],
  parent: Node,
  parentRow: SheetRow,
  parentHex: string,
  idRegistry: RowLocalId.Registry,
  byId: Map<RowLocalId, Node>,
  parentMap: Map<RowLocalId, Option<RowLocalId>>,
  metadata: SheetRow.Registry,
): void {
  for (const spec of specs) {
    const key = RowKey.virtual(parentHex, spec.preset);
    const localId = idRegistry.resolve(key);

    // Layer 2: inherit from parent
    let label = spec.label;
    let timeScale = parentRow.timescaleSetting;
    let heightPx: Option<number> = spec.heightPx ?? null;

    // Layer 3: nestedRows override for parent-stored children
    if (spec.storage === "parent") {
      // Strip trailing "-N" suffix to get the nestedRows key (e.g., "logs-0" → "logs")
      const nestedKey = spec.preset.replace(/-\d+$/, "");
      const nested = Json.child(
        Json.child(parentRow.reconciledAttributes, "nestedRows"),
        nestedKey,
      );
      if (nested) {
        const nestedLabel = Json.string(nested, "label");
        if (nestedLabel != null) label = nestedLabel;
        const nestedScale = Json.oneOf(nested, "timeScale", TIME_SCALES);
        if (nestedScale != null) timeScale = nestedScale;
        const nestedHeight = Json.number(nested, "heightPx");
        if (nestedHeight != null) heightPx = nestedHeight;
      }
    }

    const mergedAttrs = spec.attributes
      ? { ...RowTypeSpecOps.resolveParentRowId(spec.attributes, RowNetworkKey.fromWire(`r_${parentHex}`)) }
      : {};
    const attributes = spec.storage === "parent"
      ? { ...mergedAttrs, parentRowId: `r_${parentHex}` }
      : mergedAttrs;

    const node: Node = { id: localId, position: spec.position, children: [] };
    byId.set(localId, node);
    parentMap.set(localId, parent.id);

    const virtualRowDb = Network.SheetRowDb.make({
      id: RowId.of(key.toString()),
      type: spec.type,
      ownerId: UserId.of(parentRow.ownerId),
      timeScale,
      label: label ?? null,
      formula: spec.formula ?? null,
      heightPx,
      attributes: Object.keys(attributes).length > 0 ? attributes : null,
      icon: null,
      lookupKey: null,
      metadata: null,
    });
    metadata.set(localId, virtualRowDb, spec.graph ?? null);

    (parent.children as Node[]).push(node);

    if (spec.children) {
      graftChildSpecs(
        spec.children,
        node,
        parentRow,
        parentHex,
        idRegistry,
        byId,
        parentMap,
        metadata,
      );
    }
  }
}
